/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useRef } from "react";

/**
 * Full-viewport confetti canvas. `fire` is a trigger: flipping it truthy runs
 * one burst (see the `if (fire) fireConfetti()` effect below).
 *
 * The type was `any`, which is why the component had no story — the generator
 * refuses to invent a value for `any` and skipped it as "required prop the type
 * checker cannot fill". Naming the type is the fix, not an annotation: `any`
 * bought nothing here (the only call site passes `fire={true}`,
 * Tutorial.tsx:732) and it cost both the story AND the Controls row, since
 * Storybook cannot infer a control for `any` either.
 *
 * `fire` is defaulted to true below rather than left to the synthesizer's
 * `false`, because false is the one value at which this component does its job
 * by doing nothing: the canvas mounts, the animation loop runs, and not a
 * single particle is ever drawn. The story would have shown an empty panel.
 *
 * Note for whoever reads a green render-probe run against this component: the
 * probe measures the DOM, and this component's entire output is pixels painted
 * into a `<canvas>`. The canvas is laid out and painted at full viewport size
 * whether or not the burst fired, so the probe cannot tell the two apart. It is
 * the one component in this repo whose story quality is not machine-checkable.
 *
 * @story args { fire: true }
 */
export default function ConfettiCannons({ fire }: { fire: boolean }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const particles: any[] = [];
  const gravity = 0.15;

  useEffect(() => {
    const canvas = canvasRef.current!;
    const ctx = canvas.getContext("2d")!;

    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };

    resize();
    window.addEventListener("resize", resize);

    function random(min: number, max: number) {
      return Math.random() * (max - min) + min;
    }

    function createBurst(x: number, y: number, direction: number) {
      for (let i = 0; i < 220; i++) {
        const angle = random(-0.9, -2.3);
        const power = random(8, 10);

        particles.push({
          x,
          y,
          vx: Math.cos(angle) * power * direction + random(-1.5, 1.5),
          vy: Math.sin(angle) * power,
          size: random(5, 11),
          rot: random(0, Math.PI),
          vr: random(-0.25, 0.25),
          life: random(180, 320),
          flutter: random(0, Math.PI * 2),
          drift: random(-1, 1),
          chaos: random(0.002, 0.008),
          color: `hsl(${random(0, 360)}, 90%, 60%)`,
        });
      }
    }

    function fireConfetti() {
      createBurst(0, canvas.height * 0.65, 1);
      createBurst(canvas.width, canvas.height * 0.65, -1);
    }

    let wind = 0;
    let windTarget = 0;

    function update() {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      wind += (windTarget - wind) * 0.002;
      if (Math.random() < 0.01) {
        windTarget = random(-1.5, 1.5);
      }

      for (let i = particles.length - 1; i >= 0; i--) {
        const p = particles[i];
        const boost = p.life > 260 ? 1.04 : 1;

        p.vy += gravity * 0.55;
        p.vx *= boost;
        p.vy *= boost;

        const noise = Math.sin(performance.now() * p.chaos + p.flutter);

        p.vx += p.drift * 0.02 + noise * 0.08;
        p.vx += wind * 0.015;

        p.flutter += 0.12;

        p.x += p.vx + Math.sin(p.flutter) * 0.4;
        p.y += p.vy;

        p.rot += p.vr;

        ctx.save();
        ctx.translate(p.x, p.y);
        ctx.rotate(p.rot);
        ctx.fillStyle = p.color;
        ctx.fillRect(-p.size / 2, -p.size / 2, p.size, p.size * 0.6);
        ctx.restore();

        if (p.life-- <= 0 || p.y > canvas.height + 60) {
          particles.splice(i, 1);
        }
      }

      requestAnimationFrame(update);
    }

    update();

    if (fire) fireConfetti();

    return () => {
      window.removeEventListener("resize", resize);
    };
  }, [fire]);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: "fixed",
        inset: 0,
        pointerEvents: "none",
        zIndex: 99999999,
      }}
    />
  );
}
